import React, { useState, useEffect, useRef } from 'react';
import { listFiles } from '../../minio/minioAPI';
import {
  FaPlay, FaDownload, FaFileAlt, FaVideo, FaHeadphones,
  FaCalendarAlt, FaChevronRight, FaSearch, FaFilePdf,
  FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileArchive,
} from 'react-icons/fa';
import './Material.css';

const TABS = [
  { id: 'obligatorias', label: 'Mentorías Obligatorias', icon: <FaVideo /> },
  { id: 'semanales',    label: 'Mentorías Semanales',    icon: <FaCalendarAlt /> },
  { id: 'materiales',   label: 'Materiales',             icon: <FaFileAlt /> },
  { id: 'llamadas',     label: 'Llamadas',               icon: <FaHeadphones /> },
];

const fileIcon = (name) => {
  const ext = name.split('.').pop().toLowerCase();
  if (ext === 'pdf')  return <FaFilePdf className="mat-file-icon mat-file-icon--pdf" />;
  if (['doc','docx'].includes(ext)) return <FaFileWord className="mat-file-icon mat-file-icon--word" />;
  if (['xls','xlsx'].includes(ext)) return <FaFileExcel className="mat-file-icon mat-file-icon--excel" />;
  if (['ppt','pptx'].includes(ext)) return <FaFilePowerpoint className="mat-file-icon mat-file-icon--ppt" />;
  if (['zip','rar','7z'].includes(ext)) return <FaFileArchive className="mat-file-icon mat-file-icon--zip" />;
  return <FaFileAlt className="mat-file-icon" />;
};

const prettify = (name) => name.replace(/\.\w+$/, '').replace(/[-_]/g, ' ');

const Material = () => {
  const [activeTab, setActiveTab]             = useState('obligatorias');
  const [mentorias, setMentorias]             = useState([]);
  const [plusMentorias, setPlusMentorias]     = useState([]);
  const [materials, setMaterials]             = useState([]);
  const [llamadas, setLlamadas]               = useState([]);
  const [selectedMentoria, setSelectedMentoria]         = useState(null);
  const [selectedPlusMentoria, setSelectedPlusMentoria] = useState(null);
  const [search, setSearch]                   = useState('');
  const [loading, setLoading]                 = useState(true);

  const mentoriasRef = useRef(null);
  const plusRef      = useRef(null);

  const sortByLeadingNumber = (a, b) => {
    const n = (s) => { const m = s.match(/^(\d+)/); return m ? parseInt(m[1]) : 0; };
    return n(a) - n(b);
  };

  const listFilesWithDates = async (bucketUrl) => {
    const url = `${bucketUrl.replace(/\/+$/, '')}?list-type=2`;
    const res = await fetch(url);
    if (!res.ok) throw new Error(`${res.status}`);
    const xml = new window.DOMParser().parseFromString(await res.text(), 'text/xml');
    return Array.from(xml.getElementsByTagName('Contents')).map(node => ({
      key: node.getElementsByTagName('Key')[0]?.textContent || '',
      lastModified: node.getElementsByTagName('LastModified')[0]?.textContent || '',
    })).filter(o => o.key);
  };

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [llamadasFiles, plusFiles, mentoriasObjs, materialsObjs] = await Promise.all([
          listFiles('https://llamadasp.fra1.digitaloceanspaces.com'),
          listFiles('https://mentoriasobligatorias.fra1.digitaloceanspaces.com/'),
          listFilesWithDates('https://mentorias.fra1.digitaloceanspaces.com'),
          listFilesWithDates('https://filles-closer.fra1.digitaloceanspaces.com'),
        ]);

        setLlamadas(llamadasFiles);

        const sortedPlus = plusFiles.sort(sortByLeadingNumber);
        setPlusMentorias(sortedPlus);
        if (sortedPlus.length) setSelectedPlusMentoria(sortedPlus[0]);

        const sortedMentorias = [...mentoriasObjs]
          .sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified))
          .map(o => o.key);
        setMentorias(sortedMentorias);
        if (sortedMentorias.length) setSelectedMentoria(sortedMentorias[0]);

        const sortedMaterials = [...materialsObjs]
          .sort((a, b) => new Date(b.lastModified) - new Date(a.lastModified))
          .map(o => o.key);
        setMaterials(sortedMaterials);
      } catch (err) {
        console.error('Error fetching materials:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const switchVideo = (ref, setter, file) => {
    if (ref.current) {
      ref.current.pause();
      ref.current.removeAttribute('src');
      ref.current.load();
    }
    setter(file);
    setTimeout(() => ref.current?.play().catch(() => {}), 150);
  };

  const downloadFile = async (url, name) => {
    const res  = await fetch(url);
    const blob = await res.blob();
    const link = Object.assign(document.createElement('a'), {
      href: URL.createObjectURL(blob),
      download: name,
    });
    document.body.appendChild(link);
    link.click();
    link.remove();
  };

  const filteredMaterials = materials.filter(f =>
    f.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="mat-page">

      {/* ── TABS ── */}
      <div className="mat-tabs">
        {TABS.map(tab => (
          <button
            key={tab.id}
            className={`mat-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => { setActiveTab(tab.id); setSearch(''); }}
          >
            <span className="mat-tab-icon">{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      <div className="mat-body">

        {loading && (
          <div className="mat-loading">
            <div className="mat-spinner" />
            <span>Cargando contenido...</span>
          </div>
        )}

        {/* ── MENTORÍAS OBLIGATORIAS ── */}
        {!loading && activeTab === 'obligatorias' && (
          <div className="mat-video-layout">
            <div className="mat-player-wrap">
              {selectedPlusMentoria ? (
                <video
                  key={selectedPlusMentoria}
                  ref={plusRef}
                  controls
                  poster={"/Images/miniatura_video.png"}
                  className="mat-player"
                >
                  <source
                    src={`https://mentoriasobligatorias.fra1.digitaloceanspaces.com/${selectedPlusMentoria}`}
                    type="video/mp4"
                  />
                </video>
              ) : (
                <div className="mat-player-empty">Sin vídeo seleccionado</div>
              )}
              {selectedPlusMentoria && (
                <div className="mat-player-title">{prettify(selectedPlusMentoria)}</div>
              )}
            </div>
            <div className="mat-episode-list">
              <div className="mat-episode-header">
                Episodios <span className="mat-episode-count">{plusMentorias.length}</span>
              </div>
              {plusMentorias.map((f, i) => (
                <button
                  key={i}
                  className={`mat-episode-item ${selectedPlusMentoria === f ? 'active' : ''}`}
                  onClick={() => switchVideo(plusRef, setSelectedPlusMentoria, f)}
                >
                  <span className="mat-ep-num">{i + 1}</span>
                  <span className="mat-ep-name">{prettify(f)}</span>
                  {selectedPlusMentoria === f
                    ? <FaPlay className="mat-ep-icon active" />
                    : <FaChevronRight className="mat-ep-icon" />}
                </button>
              ))}
              {plusMentorias.length === 0 && (
                <p className="mat-empty">No hay vídeos disponibles.</p>
              )}
            </div>
          </div>
        )}

        {/* ── MENTORÍAS SEMANALES ── */}
        {!loading && activeTab === 'semanales' && (
          <div className="mat-video-layout">
            <div className="mat-player-wrap">
              {selectedMentoria ? (
                <video
                  key={selectedMentoria}
                  ref={mentoriasRef}
                  controls
                  poster={"/Images/miniatura_video.png"}
                  className="mat-player"
                >
                  <source
                    src={`https://mentorias.fra1.digitaloceanspaces.com/${selectedMentoria}`}
                    type="video/mp4"
                  />
                </video>
              ) : (
                <div className="mat-player-empty">Sin vídeo seleccionado</div>
              )}
              {selectedMentoria && (
                <div className="mat-player-title">{prettify(selectedMentoria)}</div>
              )}
            </div>
            <div className="mat-episode-list">
              <div className="mat-episode-header">
                Sesiones <span className="mat-episode-count">{mentorias.length}</span>
              </div>
              {mentorias.map((f, i) => (
                <button
                  key={i}
                  className={`mat-episode-item ${selectedMentoria === f ? 'active' : ''}`}
                  onClick={() => switchVideo(mentoriasRef, setSelectedMentoria, f)}
                >
                  <span className="mat-ep-num">{i + 1}</span>
                  <span className="mat-ep-name">{prettify(f)}</span>
                  {selectedMentoria === f
                    ? <FaPlay className="mat-ep-icon active" />
                    : <FaChevronRight className="mat-ep-icon" />}
                </button>
              ))}
              {mentorias.length === 0 && (
                <p className="mat-empty">No hay sesiones disponibles.</p>
              )}
            </div>
          </div>
        )}

        {/* ── MATERIALES ── */}
        {!loading && activeTab === 'materiales' && (
          <div className="mat-files-layout">
            <div className="mat-search-bar">
              <FaSearch className="mat-search-icon" />
              <input
                className="mat-search-input"
                placeholder="Buscar archivo..."
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <div className="mat-files-grid">
              {filteredMaterials.map((f, i) => (
                <div key={i} className="mat-file-card">
                  {fileIcon(f)}
                  <div className="mat-file-info">
                    <span className="mat-file-name">{prettify(f)}</span>
                    <span className="mat-file-ext">.{f.split('.').pop().toUpperCase()}</span>
                  </div>
                  <button
                    className="mat-file-dl"
                    onClick={() => downloadFile(
                      `https://filles-closer.fra1.digitaloceanspaces.com/${f}`, f
                    )}
                    title="Descargar"
                  >
                    <FaDownload />
                  </button>
                </div>
              ))}
              {filteredMaterials.length === 0 && (
                <p className="mat-empty">No se encontraron archivos.</p>
              )}
            </div>
          </div>
        )}

        {/* ── LLAMADAS ── */}
        {!loading && activeTab === 'llamadas' && (
          <div className="mat-audio-layout">
            {llamadas.map((f, i) => (
              <div key={i} className="mat-audio-card">
                <div className="mat-audio-info">
                  <FaHeadphones className="mat-audio-icon" />
                  <span className="mat-audio-name">{prettify(f)}</span>
                </div>
                <audio controls className="mat-audio-player">
                  <source
                    src={`https://llamadasp.fra1.digitaloceanspaces.com/${f}`}
                    type="audio/mpeg"
                  />
                </audio>
                <button
                  className="mat-audio-dl"
                  onClick={() => downloadFile(
                    `https://llamadasp.fra1.digitaloceanspaces.com/${f}`, f
                  )}
                >
                  <FaDownload /> Descargar
                </button>
              </div>
            ))}
            {llamadas.length === 0 && (
              <p className="mat-empty">No hay llamadas disponibles.</p>
            )}
          </div>
        )}

      </div>
    </div>
  );
};

export default Material;
